import { Tabs, Tag } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { buildTruckDetailTabs } from './build-truck-detail-tabs';
import { TRUCK_TABS, truckTabPath, type TruckTab } from './truck-tabs';
import { TRUCK_STATUS_COLORS, TRUCK_STATUS_LABELS } from './truck-status-labels';
import { useTruckCatalogues } from './use-truck-catalogues';
import { useTruckDetail } from './use-truck-detail';
import { withinSection } from '../../layouts/section-trail';
import { FLEET_TAB_LABELS, fleetTabPath } from '../fleet/fleet-tabs';

const TRUCK_TRAIL = withinSection('fleet', { label: FLEET_TAB_LABELS.trucks, to: fleetTabPath('trucks') });

/**
 * A truck's own page: fields, odometer, documents, ownership and history each on their own
 * tab. `truck.manage` mutates the record, odometer and ownership transfer; `document.manage`
 * mutates documents; `fleet.view` alone sees everything read-only (CLAUDE.md: nav-hiding and
 * disabled controls are UX only, the server remains the authority).
 */
export function TruckDetailPage({ tab }: { tab: TruckTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detail = useTruckDetail(id);
  const catalogues = useTruckCatalogues();
  const { state: currentUser } = useCurrentUser();
  const canManageTruck = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('truck.manage');
  const canManageDocuments = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('document.manage');

  if (detail.state.status === 'forbidden') return <Navigate to="/" replace />;

  if (detail.state.status === 'loading') return <DetailPageSkeleton label="Loading truck" breadcrumbs={TRUCK_TRAIL} tabs={TRUCK_TABS.length} />;

  if (detail.state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Truck not found" description="This truck doesn't exist, or the link is out of date." />;
  }

  if (detail.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this truck" description="Something went wrong loading this truck. Try reloading the page." />;
  }

  const { truck } = detail.state;
  const truckClasses = catalogues.status === 'loaded' ? catalogues.truckClasses : [];

  return (
    <>
      <PageHeader
        breadcrumbs={TRUCK_TRAIL}
        title={truck.registration}
        description={`${truck.make} ${truck.model}`}
        actions={<Tag color={TRUCK_STATUS_COLORS[truck.status]}>{TRUCK_STATUS_LABELS[truck.status]}</Tag>}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(truckTabPath(truck.id, key as TruckTab))}
        items={buildTruckDetailTabs({ truck, truckClasses, canManageTruck, canManageDocuments, detail })}
      />
    </>
  );
}
